<script setup lang="ts">
import { computed, ref } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { BlockMedia, PageBlock, MediaType } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Media from "@/views/blocks/shared/section/media/media/Media.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

import type { WithPanelProps } from "./WithPanelProps";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<WithPanelProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const selectedImageIndex = ref(0);

const defaultImage: BlockMedia = {
    mediaType: MediaType.IMAGE,
    src: "",
    alt: "Select an image from the gallery",
    videoSrc: "",
    videoAutoplay: false,
    videoControls: false,
    videoLoop: false,
    iframeSrc: "",
};

const selectedImage = computed(() => {
    return dataValues.value.images.data.items.length > selectedImageIndex.value
        ? dataValues.value.images.data.items[selectedImageIndex.value]
        : defaultImage;
});

const onPanelImageClick = (imageIndex: number) => {
    selectedImageIndex.value = imageIndex;
};
</script>

<template>
    <Section
        :id="block.webId"
        :background-color="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto space-y-8 sm:space-y-16">
                <!-- Header Section -->
                <div
                    class="max-w-2xl flex flex-col gap-4 text-center mx-auto"
                    v-if="
                        !dataValues.tag.isHidden ||
                        !dataValues.title.isHidden ||
                        !dataValues.description.isHidden
                    "
                >
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <Heading
                            :text="dataValues.tag.data.text"
                            :tag="dataValues.tag.data.tag"
                            :text-color="styleValues.brand"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :text-color="styleValues.text"
                            class="text-4xl sm:text-5xl"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :text-color="styleValues.text"
                            class="text-lg opacity-70"
                        />
                    </EditableElement>
                </div>

                <!-- Gallery with Lightbox -->
                <div
                    class="grid grid-cols-4 gap-4 lg:grid-cols-8 lg:grid-rows-4 sm:gap-8"
                    v-if="dataValues.images.data.items.length > 0"
                >
                    <!-- Large left image -->
                    <div class="col-span-4 lg:col-span-7 lg:row-span-4">
                        <Transition name="image-fade" mode="out-in">
                            <div
                                :key="selectedImage.src"
                                class="w-full h-full aspect-[16/9] rounded-lg sm:rounded-3xl overflow-hidden"
                            >
                                <Media
                                    :data="selectedImage"
                                    :image-width="1116"
                                    :image-height="628"
                                    :video-width="1116"
                                    :video-height="628"
                                />
                            </div>
                        </Transition>
                    </div>

                    <!-- Small right images -->
                    <EditableElement
                        v-for="(image, index) in dataValues.images.data.items"
                        :key="image.id"
                        :blockId="block.id"
                        :field-name="`images_${image.id}`"
                        :hideable="false"
                        :onSelect="() => onPanelImageClick(index)"
                    >
                        <button
                            class="aspect-square rounded-lg sm:rounded-3xl cursor-pointer hover:opacity-80 border-2 border-transparent focus:outline-none overflow-hidden panel-button"
                            :class="{ 'active-button': index === selectedImageIndex }"
                            :aria-label="`Select media ${index + 1}: ${
                                !image.mediaType || image.mediaType === MediaType.IMAGE
                                    ? image.alt
                                    : ''
                            }`"
                            :aria-pressed="index === selectedImageIndex"
                            @click="onPanelImageClick(index)"
                        >
                            <div class="pointer-events-none w-full h-full">
                                <Media
                                    :data="image"
                                    :image-width="128"
                                    :image-height="128"
                                    :video-width="128"
                                    :video-height="128"
                                    tabindex="-1"
                                />
                            </div>
                        </button>
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.active-button {
    border-color: v-bind("styleValues.brand");
}

.panel-button {
    transition: border-color 0.2s ease-in-out;
}

.panel-button:focus-visible {
    border-style: dashed;
    border-color: v-bind("styleValues.brand");
}

.image-fade-enter-active,
.image-fade-leave-active {
    transition: opacity 0.2s ease-in-out;
}

.image-fade-enter-from,
.image-fade-leave-to {
    opacity: 0;
}
</style>
